import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { merge } from "lodash-es";

import {
  EventBusTravelLegForm,
  EventFlightTravelLegForm,
  EventOtherTravelLegForm,
} from "Bands/components";
import {
  BAND_ACTIONS,
  EventTravelLegType,
  IEventTravelLegParams,
  TravelLegType,
} from "Bands/types";
import { copy } from "shared/assets";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { editTravelLeg, dataMetaSelector } from "Bands/state";
import { addTemporaryToast } from "shared/components/Toast";
import { Modal } from "shared/components";
import { useClearActionMetaOnUnMount } from "Bands/utils";

const editCopy = copy.travelBlock.travelLeg.edit;

interface Props {
  blockId: string;
  travelLeg: EventTravelLegType;
  travelLegType: TravelLegType;
  handleClose: () => void;
}

export const EditTravelLegModal: React.FC<Props> = ({
  blockId,
  travelLeg,
  travelLegType,
  handleClose,
}) => {
  const dispatch = useDispatch();

  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editTravelLeg)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.editTravelLeg);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editCopy.messages.success,
        })
      );

      handleClose();
    }
  }, [apiMeta?.status, dispatch, handleClose]);

  const handleSubmit = useCallback(
    (data: IEventTravelLegParams) => {
      if (!travelLeg.id) return;

      dispatch(
        editTravelLeg({
          id: travelLeg.id,
          data,
          blockId,
          travelLegType,
        })
      );
    },
    [blockId, dispatch, travelLegType, travelLeg]
  );

  const defaultValues: IEventTravelLegParams = useMemo(
    () =>
      merge({}, travelLeg, {
        departure: {
          location: travelLeg.departure.location.data.attributes.id,
        },
        arrival: {
          location: travelLeg.arrival.location.data.attributes.id,
        },
      }),
    [travelLeg]
  );

  const buttons: IFormButtons = useMemo(
    () => ({
      secondary: {
        onClick: handleClose,
      },
    }),
    [handleClose]
  );

  const formProps = useMemo(
    () => ({
      defaultValues,
      buttons,
      handleSubmit,
      isSubmitting: apiMeta?.status === IAPIState.pending,
      apiError: apiMeta?.error,
      error: !!apiMeta?.error,
      defaultSuggestedLocations: [
        travelLeg.departure.location.data.attributes,
        travelLeg.arrival.location.data.attributes,
      ],
    }),
    [
      apiMeta?.error,
      apiMeta?.status,
      buttons,
      defaultValues,
      handleSubmit,
      travelLeg.arrival,
      travelLeg.departure,
    ]
  );

  return (
    <Modal title={editCopy.title}>
      {travelLegType === TravelLegType.flight && (
        <EventFlightTravelLegForm {...formProps} />
      )}
      {(travelLegType === TravelLegType.bus ||
        travelLegType === TravelLegType.train) && (
        <EventBusTravelLegForm {...formProps} />
      )}
      {travelLegType === TravelLegType.other && (
        <EventOtherTravelLegForm {...formProps} />
      )}
    </Modal>
  );
};
